import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Select,
  MenuItem,
  Button,
  TextField,
  FormLabel,
  Collapse,
  Alert,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchCategories,
  setSelectedCategory,
  resetCategoryData,
  addCategory,
  editCategory,
  deleteCategory,
  toggleCategoryType,
} from "../reducers/ruleFrameSlice";
import NormalRuleFrame from "./NormalRuleFrame";
import AdvancedRuleFrame from "./AdvancedRuleFrame";
import { useLocation, useNavigate } from "react-router-dom";

const RuleFrame = () => {
  const dispatch = useDispatch();
  const { error, categories, selectedCategory, categoryType, deploymentType } =
    useSelector((state) => state.ruleFrame);

  const license_key = useSelector(
    (state) => state.authen.loggedInUser?.license_key,
  );

  const [newCategory, setNewCategory] = useState("");
  const [editName, setEditName] = useState(""); // <-- For editing
  const [isEditing, setIsEditing] = useState(false);

  const has_write = useSelector(
    (state) => state.authen.loggedInUser?.license_role === "Change",
  );

  const has_advanced = useSelector(
    (state) => state.authen.loggedInUser?.advanced_licenses?.pathology,
  );

  const location = useLocation();
  const navigate = useNavigate();

  const previousState = location.state;

  // Load categories initially
  useEffect(() => {
    if (license_key) {
      dispatch(fetchCategories(license_key));
    }
  }, [dispatch, license_key]);

  const isBasicMode =
    selectedCategory && categoryType[selectedCategory] === false;

  const handleBack = () => {
    if (previousState?.from) {
      navigate(previousState.from, {
        state: previousState,
      });
    } else {
      navigate(-1);
    }
  };
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {/* First Paper */}
      <Paper
        elevation={2}
        sx={{
          p: 2,
          display: "flex",
          alignItems: "center",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <FormLabel>Pathology Test Categories</FormLabel>

        <Select
          size="small"
          value={selectedCategory || ""}
          onChange={(e) => {
            dispatch(setSelectedCategory(e.target.value));
            setIsEditing(false); // reset edit mode if switching
          }}
        >
          {categories.map((cat) => (
            <MenuItem key={cat} value={cat}>
              {cat}
            </MenuItem>
          ))}
        </Select>

        {deploymentType !== "runner" && (
          <>
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                const result = window.confirm(
                  `Are you sure you want to delete ${selectedCategory}?`,
                );
                if (result) {
                  dispatch(
                    deleteCategory({ category: selectedCategory, license_key }),
                  );
                } else {
                  window.alert("The action has been cancelled.");
                }
              }}
              disabled={!selectedCategory || !has_write}
            >
              Delete
            </Button>

            {!isEditing ? (
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  setIsEditing(true);
                  setEditName(selectedCategory); // pre-fill with old name
                }}
                disabled={!selectedCategory || !has_write}
              >
                Edit
              </Button>
            ) : (
              <>
                <TextField
                  size="small"
                  label="New Name"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                />
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => {
                    if (editName.trim()) {
                      dispatch(
                        editCategory({
                          oldName: selectedCategory,
                          newName: editName.trim(),
                          license_key,
                        }),
                      );
                      setIsEditing(false);
                    }
                  }}
                  disabled={!has_write}
                >
                  Save
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setIsEditing(false)}
                >
                  Cancel
                </Button>
              </>
            )}

            <TextField
              size="small"
              label="New Category"
              value={newCategory}
              onChange={(e) => setNewCategory(e.target.value)}
              disabled={!has_write}
            />
            <Button
              size="small"
              variant="contained"
              disabled={!has_write}
              onClick={() => {
                if (newCategory.trim()) {
                  dispatch(
                    addCategory({ category: newCategory.trim(), license_key }),
                  );
                  setNewCategory("");
                }
              }}
            >
              Add
            </Button>
          </>
        )}

        {selectedCategory && (
          <Button
            size="small"
            variant="contained"
            color={isBasicMode ? "secondary" : "primary"}
            disabled={!has_write || (isBasicMode && !has_advanced)}
            onClick={() => dispatch(toggleCategoryType(selectedCategory))}
          >
            {isBasicMode ? "Switch to Advanced Mode" : "Switch to Basic Mode"}
          </Button>
        )}

        <Button
          size="small"
          variant="contained"
          color="secondary"
          disabled={!has_write}
          onClick={() => dispatch(resetCategoryData({ license_key }))}
        >
          Legacy Reset
        </Button>

        <Button
          size="small"
          variant="contained"
          onClick={() =>
            navigate(previousState?.from ?? -1, {
              state: previousState,
            })
          }
        >
          Back
        </Button>

        <Collapse in={Boolean(error)}>
          {error && (
            <Alert severity="error" variant="filled">
              {error}
            </Alert>
          )}
        </Collapse>
      </Paper>

      {isBasicMode && <NormalRuleFrame />}
      {!isBasicMode && <AdvancedRuleFrame />}
    </Box>
  );
};

export default RuleFrame;
